import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Clock, Gauge, PieChart, UserX, Users } from 'lucide-react'
import { Badge, Bone } from '@/components/ui'
import { cn } from '@/lib/cn'
import type {
  OrganizationDashboard,
  OrganizationDashboardCompany,
  OrganizationDashboardSummary,
} from '@/types/dashboard.types'
import { DashboardDetailModal, DashboardTile } from './DashboardTile'
import {
  ATTENDANCE_GOOD,
  ATTENDANCE_WARN,
  attendanceBarColor,
  attendanceTone,
  formatMinutes,
  formatNumber,
  formatPct,
} from './dashboardFormat'

// Status palette — color follows STATE, reserved and consistent across the
// dashboard. "Not scheduled" is neutral: it is never an absence.
const MIX_SEGMENTS = [
  { key: 'present', label: 'Present', color: '#004a6b', dotClass: 'bg-primary dark:bg-[#4aa3c4]' },
  { key: 'late', label: 'Late', color: '#e09a00', dotClass: 'bg-amber-500' },
  { key: 'halfDay', label: 'Half-day', color: '#5fa8c4', dotClass: 'bg-[#5fa8c4]' },
  { key: 'onLeave', label: 'On leave', color: '#a9c9d8', dotClass: 'bg-[#a9c9d8]' },
  { key: 'absent', label: 'Absent', color: '#ff4d6d', dotClass: 'bg-accent' },
  { key: 'dueLater', label: 'Due later', color: '#c7cbd3', dotClass: 'bg-gray-300' },
  {
    key: 'notScheduled',
    label: 'Not scheduled',
    color: '#e7e9ee',
    dotClass: 'bg-gray-200 dark:bg-gray-600',
  },
] as const

type MixKey = (typeof MIX_SEGMENTS)[number]['key']

type MixSource = Pick<
  OrganizationDashboardSummary,
  | 'presentToday'
  | 'lateToday'
  | 'halfDayToday'
  | 'onLeaveToday'
  | 'absentToday'
  | 'dueLaterToday'
  | 'notScheduledToday'
>

/** One field per segment. `?? 0` keeps an older server's missing field from becoming NaN. */
function mixValue(source: MixSource | undefined, key: MixKey): number {
  if (!source) return 0
  const fields: Record<MixKey, number | undefined> = {
    present: source.presentToday,
    late: source.lateToday,
    halfDay: source.halfDayToday,
    onLeave: source.onLeaveToday,
    absent: source.absentToday,
    dueLater: source.dueLaterToday,
    notScheduled: source.notScheduledToday,
  }
  return fields[key] ?? 0
}

function MixBar({
  source,
  className,
}: {
  source: MixSource
  className?: string
}): React.ReactElement {
  const segments = MIX_SEGMENTS.map((s) => ({ ...s, value: mixValue(source, s.key) }))
  const total = segments.reduce((sum, s) => sum + s.value, 0)
  return (
    <div
      className={cn(
        'flex w-full gap-0.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-900',
        className
      )}
    >
      {total > 0
        ? segments
            .filter((s) => s.value > 0)
            .map((s) => (
              <div
                key={s.key}
                style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
                title={`${s.label}: ${formatNumber(s.value)}`}
              />
            ))
        : null}
    </div>
  )
}

/** Today's workforce composition as one segmented bar plus a count per state. */
export function AttendanceMixCard({
  summary,
  isLoading,
  onExpand,
}: {
  summary: OrganizationDashboardSummary | undefined
  isLoading: boolean
  onExpand: () => void
}): React.ReactElement {
  const segments = MIX_SEGMENTS.map((s) => ({ ...s, value: mixValue(summary, s.key) }))
  const total = segments.reduce((sum, s) => sum + s.value, 0)
  const scheduled = summary?.scheduledToday ?? 0
  const absent = summary?.absentToday ?? 0
  // Absence is measured against the people who were scheduled, not the roster.
  const absentPct = scheduled > 0 ? Math.round((absent / scheduled) * 100) : 0

  return (
    <DashboardTile
      title="Today's attendance mix"
      subtitle={
        total > 0
          ? `${formatNumber(scheduled)} of ${formatNumber(total)} staff have a shift today`
          : "Waiting for today's first attendance update"
      }
      icon={PieChart}
      onExpand={onExpand}
      actions={
        !isLoading && total > 0 ? (
          absentPct >= 50 ? (
            <Badge variant="danger" size="sm">
              <AlertTriangle className="mr-1 h-3 w-3" aria-hidden />
              {absentPct}% absent
            </Badge>
          ) : (
            <Badge variant="success" size="sm">
              <CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
              Coverage stable
            </Badge>
          )
        ) : null
      }
      bodyClassName="px-5 pb-5"
    >
      {isLoading ? (
        <div className="space-y-4">
          <Bone className="h-2.5 w-full rounded-full" />
          <Bone className="h-24 w-full rounded-lg" />
        </div>
      ) : total === 0 ? (
        <div className="grid h-40 place-items-center text-sm text-gray-500 dark:text-gray-400">
          No staff recorded today.
        </div>
      ) : (
        <div className="space-y-4">
          <MixBar source={summary as MixSource} className="h-2.5" />
          <dl className="grid grid-cols-3 gap-x-4 gap-y-3">
            {segments.map((s) => (
              <div key={s.key} className="min-w-0">
                <dt className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', s.dotClass)} aria-hidden />
                  <span className="leading-tight">{s.label}</span>
                </dt>
                <dd
                  className={cn(
                    'mt-0.5 font-display text-lg font-semibold tabular-nums',
                    s.value === 0
                      ? 'text-gray-300 dark:text-gray-600'
                      : 'text-gray-900 dark:text-gray-100'
                  )}
                >
                  {formatNumber(s.value)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </DashboardTile>
  )
}

export function AttendanceMixModal({
  open,
  data,
  onClose,
}: {
  open: boolean
  data: OrganizationDashboard | undefined
  onClose: () => void
}): React.ReactElement | null {
  if (!open) return null
  const companies = [...(data?.companyBreakdown ?? [])].sort((a, b) => a.name.localeCompare(b.name))
  return (
    <DashboardDetailModal
      open
      onClose={onClose}
      title="Attendance mix by company"
      subtitle="Each bar is one company's roster today. Teal attended, coral absent, gray not due or not scheduled."
    >
      <div className="mb-5 flex flex-wrap gap-x-4 gap-y-2">
        {MIX_SEGMENTS.map((s) => (
          <span
            key={s.key}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
          >
            <span className={cn('h-2 w-2 rounded-full', s.dotClass)} aria-hidden />
            {s.label}
          </span>
        ))}
      </div>
      {data ? (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700/70">
          <li className="grid grid-cols-[minmax(0,12rem)_minmax(0,1fr)_4rem] items-center gap-4 pb-3">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              All companies
            </span>
            <MixBar source={data.summary} className="h-3" />
            <span className="text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
              {formatPct(data.summary.attendancePct)}
            </span>
          </li>
          {companies.map((company) => (
            <li
              key={company.companyId}
              className="grid grid-cols-[minmax(0,12rem)_minmax(0,1fr)_4rem] items-center gap-4 py-2.5"
            >
              <Link
                to={`/companies/${company.companyId}`}
                className="truncate text-sm text-gray-700 hover:text-primary hover:underline dark:text-gray-200 dark:hover:text-sky-300"
              >
                {company.name}
              </Link>
              <MixBar source={company} className="h-2" />
              <span
                className={cn(
                  'text-right text-sm tabular-nums',
                  attendanceTone(company.attendancePct ?? null)
                )}
              >
                {formatPct(company.attendancePct)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <Bone className="h-64 w-full rounded-lg" />
      )}
    </DashboardDetailModal>
  )
}

interface CoverageRow {
  companyId: string
  name: string
  pct: number
  scheduled: number
}

function coverageRows(companies: OrganizationDashboardCompany[]): CoverageRow[] {
  return companies
    .flatMap((c) =>
      c.attendancePct == null
        ? []
        : [
            {
              companyId: c.companyId,
              name: c.name,
              scheduled: c.scheduledToday ?? 0,
              pct: c.attendancePct,
            },
          ]
    )
    .sort((a, b) => a.pct - b.pct || a.name.localeCompare(b.name))
}

function coverageStatus(pct: number): string {
  if (pct >= ATTENDANCE_GOOD) return 'Healthy'
  if (pct >= ATTENDANCE_WARN) return 'Watch'
  return 'Needs action'
}

function CoverageList({ rows }: { rows: CoverageRow[] }): React.ReactElement {
  return (
    <ul className="divide-y divide-gray-100 dark:divide-gray-700/70">
      {rows.map((row) => (
        <li key={row.companyId} className="grid gap-1.5 py-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <Link
              to={`/companies/${row.companyId}`}
              className="min-w-0 truncate text-sm font-medium text-gray-900 hover:text-primary hover:underline dark:text-gray-100 dark:hover:text-sky-300"
            >
              {row.name}
            </Link>
            <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
              {formatNumber(row.scheduled)} due ·{' '}
              <span className={cn('font-semibold tabular-nums', attendanceTone(row.pct))}>
                {row.pct}%
              </span>{' '}
              <span className="sr-only">{coverageStatus(row.pct)}</span>
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-900">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.max(row.pct, 2)}%`,
                backgroundColor: attendanceBarColor(row.pct),
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

/** The companies with the weakest coverage today, so the worst one jumps out. */
export function CompanyCoverageCard({
  companies,
  isLoading,
  onExpand,
}: {
  companies: OrganizationDashboardCompany[]
  isLoading: boolean
  onExpand: () => void
}): React.ReactElement {
  const rows = useMemo(() => coverageRows(companies).slice(0, 5), [companies])
  return (
    <DashboardTile
      title="Lowest attendance today"
      subtitle="Share of staff due so far who attended"
      icon={Gauge}
      onExpand={onExpand}
      bodyClassName="px-5 pb-3"
    >
      {isLoading ? (
        <Bone className="h-40 w-full rounded-lg" />
      ) : rows.length === 0 ? (
        <div className="grid h-40 place-items-center text-center text-sm text-gray-500 dark:text-gray-400">
          No one is due across companies yet today.
        </div>
      ) : (
        <CoverageList rows={rows} />
      )}
    </DashboardTile>
  )
}

export function CompanyCoverageModal({
  open,
  data,
  onClose,
}: {
  open: boolean
  data: OrganizationDashboard | undefined
  onClose: () => void
}): React.ReactElement | null {
  if (!open) return null
  const rows = coverageRows(data?.companyBreakdown ?? [])
  const quiet = (data?.companyBreakdown.length ?? 0) - rows.length
  return (
    <DashboardDetailModal
      open
      onClose={onClose}
      title="Attendance by company"
      subtitle={`Lowest first. Green from ${ATTENDANCE_GOOD}%, amber from ${ATTENDANCE_WARN}%.${quiet > 0 ? ` ${quiet} ${quiet === 1 ? 'company has' : 'companies have'} nobody due yet and ${quiet === 1 ? 'is' : 'are'} left out.` : ''}`}
    >
      {rows.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
          No one is due across companies yet today.
        </p>
      ) : (
        <div className="grid gap-x-10 lg:grid-cols-2">
          <CoverageList rows={rows.slice(0, Math.ceil(rows.length / 2))} />
          <CoverageList rows={rows.slice(Math.ceil(rows.length / 2))} />
        </div>
      )}
    </DashboardDetailModal>
  )
}

const CARD_OUTLIERS = 5

function OutlierLists({
  data,
  companyName,
  limit,
}: {
  data: OrganizationDashboard
  companyName: (id: string) => string
  limit?: number
}): React.ReactElement {
  const noShows = data.outliers.noShowsToday.slice(0, limit)
  const late = data.outliers.lateThisWeek.slice(0, limit)
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <section aria-labelledby="watch-noshows">
        <h4
          id="watch-noshows"
          className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-[#d92d50] dark:text-rose-300"
        >
          <UserX className="h-3.5 w-3.5" aria-hidden />
          No-shows today
        </h4>
        {noShows.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Nobody missing right now.</p>
        ) : (
          <ul className="space-y-2">
            {noShows.map((p) => (
              <li
                key={`${p.userId}:${p.companyId}`}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                    {p.name || p.employeeId || 'Unknown'}
                  </span>
                  <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                    {companyName(p.companyId)}
                  </span>
                </span>
                {p.shifts > 1 ? (
                  <Badge variant="danger" size="sm">
                    {p.shifts} shifts
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="watch-late">
        <h4
          id="watch-late"
          className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400"
        >
          <Clock className="h-3.5 w-3.5" aria-hidden />
          Most late this week
        </h4>
        {late.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">No late arrivals this week.</p>
        ) : (
          <ul className="space-y-2">
            {late.map((p) => (
              <li
                key={`${p.userId}:${p.companyId}`}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                    {p.name || p.employeeId || 'Unknown'}
                  </span>
                  <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                    {companyName(p.companyId)} · {p.lateDays}d late
                  </span>
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-amber-700 dark:text-amber-400">
                  {formatMinutes(p.lateMinutes)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

/** People with no-shows today and the most late minutes this week. */
export function PeopleToWatchCard({
  data,
  isLoading,
  companyName,
  onExpand,
}: {
  data: OrganizationDashboard | undefined
  isLoading: boolean
  companyName: (id: string) => string
  onExpand: () => void
}): React.ReactElement {
  return (
    <DashboardTile
      title="People to watch"
      icon={Users}
      onExpand={onExpand}
      bodyClassName="px-5 pb-5"
    >
      {isLoading || !data ? (
        <Bone className="h-40 w-full rounded-lg" />
      ) : (
        <OutlierLists data={data} companyName={companyName} limit={CARD_OUTLIERS} />
      )}
    </DashboardTile>
  )
}

export function PeopleToWatchModal({
  open,
  data,
  companyName,
  onClose,
}: {
  open: boolean
  data: OrganizationDashboard | undefined
  companyName: (id: string) => string
  onClose: () => void
}): React.ReactElement | null {
  if (!open) return null
  return (
    <DashboardDetailModal
      open
      onClose={onClose}
      title="People to watch"
      subtitle="Everyone with a missed shift right now, and the people with the most minutes past grace this week."
    >
      {data ? (
        <OutlierLists data={data} companyName={companyName} />
      ) : (
        <Bone className="h-64 w-full rounded-lg" />
      )}
    </DashboardDetailModal>
  )
}
